import { type ThreeEvent, useThree } from '@react-three/fiber';
import { type RefObject, useCallback, useRef } from 'react';
import { useShallow } from 'zustand/shallow';

import { cloneArticleInstances, isFramiClamp } from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import {
    getAnchorPart,
    instanceToPoiIndexMap,
    isTopBottomCameraView,
    raycastPlane,
    updateInstanceToArticleIdMap,
    updateInstanceToPoiIndexMap
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/dragUtils';
import { getConnectorRequestPoiIndex } from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { isSceneToolActive } from 'app/modules/wallforming/components/scenes/objects/utils/sceneUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { isBoxSelectionActive } from 'app/modules/wallforming/components/state-management/placement/slices/scenes/sceneEventHandlerSlice';

import type { ArticleReference } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleReference';

/**
 * Handles pointer and selection events for batched article meshes, resolving batch and instance references.
 */
export const useBatchedMeshEventHandler = () => {
    const {
        physicalBatchToInstance,
        sheetBatchToInstance,
        holesBatchToInstance,
        handleOnArticleSelection,
        handleOnArticlePointerOver,
        clearHoveredArticleHighlight,
        articleDragRefsMap,
        isDraggingActive,
        isTransformationActiveRef
    } = usePlacementStore(
        useShallow((state) => ({
            physicalBatchToInstance: state.physicalBatchToInstance,
            sheetBatchToInstance: state.sheetBatchToInstance,
            holesBatchToInstance: state.holesBatchToInstance,
            handleOnArticleSelection: state.handleOnArticleSelection,
            handleOnArticlePointerOver: state.handleOnArticlePointerOver,
            clearHoveredArticleHighlight: state.clearHoveredArticleHighlight,
            articleDragRefsMap: state.articleDragRefsMap,
            isDraggingActive: state.isDraggingActive,
            isTransformationActiveRef: state.isTransformationActiveRef
        }))
    );

    const gl = useThree((state) => state.gl);
    const hoveredInstanceIdRef = useRef<string | null>(null);
    const instanceIndexCacheRef = useRef<WeakMap<object, Map<string, number>>>(new WeakMap());

    // resolve batchId → instanceId → dragRef → patchedEvent
    const resolveBatchEvent = useCallback(
        (e: ThreeEvent<PointerEvent>) => {
            const batchInstanceId = (e as ThreeEvent<PointerEvent> & { batchId?: number }).batchId;
            if (batchInstanceId === undefined) {
                return null;
            }

            const state = usePlacementStore.getState();
            let articleInstanceId: string | undefined;

            if (e.object === state.batchedMeshes.physical) {
                articleInstanceId = physicalBatchToInstance.get(batchInstanceId);
            } else if (e.object === state.batchedMeshes.sheet) {
                articleInstanceId = sheetBatchToInstance.get(batchInstanceId);
            } else if (e.object === state.batchedMeshes.holes) {
                articleInstanceId = holesBatchToInstance.get(batchInstanceId);
            }

            if (!articleInstanceId) {
                return null;
            }

            const articleId = state.instanceBatchIds.get(articleInstanceId)?.articleId;
            if (!articleId) {
                return null;
            }

            let dragRef = articleDragRefsMap.current.get(articleId);
            if (!dragRef?.articleRef?.current?.articleInstances[articleInstanceId]) {
                const article = state.articles[articleId];
                if (!article) {
                    return null;
                }

                if (!article.articleInstances[articleInstanceId]) {
                    return null;
                }

                // Fallback reference, so that batched meshes remain immediately raycastable.
                const fallbackArticleRef = {
                    current: {
                        id: articleId,
                        articleNumber: article.articleNumber,
                        isMainCanvas: true,
                        lifeCycleCode: article.lifeCycleCode,
                        articleRepresentations: dragRef?.articleRef?.current?.articleRepresentations ?? [],
                        articleInstances: cloneArticleInstances(article),
                        edgeGeometries: article.edgeGeometries
                    }
                } as RefObject<ArticleReference | undefined>;

                dragRef = { articleRef: fallbackArticleRef };
            }

            const articleInstances = dragRef.articleRef.current?.articleInstances;
            if (!articleInstances) {
                return null;
            }

            // articleInstances may be mutated in place (batch processing), so rebuild the cached lookup on a miss
            const cacheKey = articleInstances as object;
            let instanceLookup = instanceIndexCacheRef.current.get(cacheKey);
            if (!instanceLookup?.has(articleInstanceId)) {
                instanceLookup = new Map<string, number>();
                const keys = Object.keys(articleInstances);
                for (let i = 0; i < keys.length; i++) {
                    instanceLookup.set(keys[i], i);
                }
                instanceIndexCacheRef.current.set(cacheKey, instanceLookup);
            }

            const instanceIndex = instanceLookup.get(articleInstanceId) ?? -1;
            if (instanceIndex === -1) {
                return null;
            }

            const patchedEvent = Object.assign(Object.create(Object.getPrototypeOf(e)), e, {
                instanceId: instanceIndex
            });

            return { patchedEvent, dragRef, articleInstanceId };
        },
        [articleDragRefsMap, holesBatchToInstance, physicalBatchToInstance, sheetBatchToInstance]
    );

    const handleOnPointerDown = useCallback(
        (e: ThreeEvent<PointerEvent>) => {
            if (e.button !== 0 || isTransformationActiveRef.current) {
                return;
            }
            if (gl.domElement.style.cursor !== 'move') {
                gl.domElement.style.setProperty('cursor', 'move');
            }

            const resolved = resolveBatchEvent(e);
            if (!resolved) {
                return;
            }

            const currentState = usePlacementStore.getState();
            if (
                resolved.articleInstanceId &&
                !currentState.waitForPlacementData &&
                currentState.prepareManualConnectorDrag
            ) {
                const draggedArticleId = resolved.dragRef.articleRef.current?.id;
                if (draggedArticleId) {
                    const articleNumber = currentState.articles[draggedArticleId]?.articleNumber;
                    if (isFramiClamp(articleNumber) && !currentState.manualPlacementData.isLoading) {
                        const isTopBottomView = isTopBottomCameraView(raycastPlane.normal);
                        if (!isTopBottomView) {
                            const instance =
                                currentState.articles[draggedArticleId]?.articleInstances[resolved.articleInstanceId];
                            const part = getAnchorPart(instance);

                            updateInstanceToArticleIdMap([resolved.articleInstanceId], currentState);
                            updateInstanceToPoiIndexMap([resolved.articleInstanceId], currentState);
                            const legacyPoiIndex = instanceToPoiIndexMap.get(resolved.articleInstanceId);
                            const connectorPoiGuids = instance?.poiGuids?.length
                                ? instance.poiGuids
                                : legacyPoiIndex !== undefined
                                  ? [currentState.pois[legacyPoiIndex].poi.poiGuid]
                                  : [];
                            const selectedPoiIndex = currentState.poiSelection.current.index;
                            const manualSnapPoiIndex = getConnectorRequestPoiIndex(
                                currentState.pois,
                                undefined,
                                connectorPoiGuids
                            );
                            const poiIndex = selectedPoiIndex ?? manualSnapPoiIndex;

                            if (poiIndex !== undefined && part) {
                                usePlacementStore.setState({ waitForPlacementData: true });
                                void currentState.prepareManualConnectorDrag({
                                    articleId: draggedArticleId,
                                    articleNumber,
                                    poiIndex,
                                    manualSnapPoiIndex,
                                    connectorReference: { position: part.position.clone() }
                                });
                            }
                        }
                    }
                }
            }

            handleOnArticleSelection(resolved.patchedEvent, resolved.dragRef.articleRef);
            e.stopPropagation();
        },
        [isTransformationActiveRef, gl.domElement.style, resolveBatchEvent, handleOnArticleSelection]
    );

    const handleOnPointerMove = useCallback(
        (e: ThreeEvent<PointerEvent>) => {
            if (
                e.buttons !== 0 ||
                isDraggingActive() ||
                isBoxSelectionActive() ||
                isSceneToolActive(usePlacementStore.getState())
            ) {
                if (hoveredInstanceIdRef.current !== null) {
                    hoveredInstanceIdRef.current = null;
                    clearHoveredArticleHighlight();
                }
                return;
            }

            const resolved = resolveBatchEvent(e);
            if (!resolved) {
                return;
            }

            if (gl.domElement.style.cursor !== 'pointer') {
                gl.domElement.style.setProperty('cursor', 'pointer');
            }

            hoveredInstanceIdRef.current = resolved.articleInstanceId;
            handleOnArticlePointerOver(resolved.patchedEvent, resolved.dragRef.articleRef);
        },
        [
            clearHoveredArticleHighlight,
            isDraggingActive,
            resolveBatchEvent,
            gl.domElement.style,
            handleOnArticlePointerOver
        ]
    );

    const handleOnPointerOver = useCallback(
        (e: ThreeEvent<PointerEvent>) => {
            handleOnPointerMove(e);
        },
        [handleOnPointerMove]
    );

    const handleOnPointerOut = useCallback(() => {
        if (!isSceneToolActive(usePlacementStore.getState()) && gl.domElement.style.cursor !== 'auto') {
            gl.domElement.style.setProperty('cursor', 'auto');
        }

        if (hoveredInstanceIdRef.current === null) {
            return;
        }
        hoveredInstanceIdRef.current = null;
        clearHoveredArticleHighlight();
    }, [gl.domElement.style, clearHoveredArticleHighlight]);

    return { handleOnPointerDown, handleOnPointerMove, handleOnPointerOver, handleOnPointerOut };
};
